import { EntityLinks, Goal, ID } from '@griply/types';

import { getGoalParentIds, GoalParentSource } from './get-goal-parents';

export type GetPresentParentIdsOptions = {
  links: EntityLinks;
  // Lets the fallback climb the migrated graph. Without it a migrated goal whose parents were
  // all filtered out renders as a root instead of nesting under a present ancestor.
  goalsById?: Map<ID, GoalParentSource>;
};

// Breadth-first so the nearest present ancestor wins, and cycle-safe: corrupt parent links
// would otherwise recurse forever.
const findPresentAncestorId = (
  parentIds: ID[],
  isPresent: (id: ID) => boolean,
  resolveParents: (id: ID) => ID[],
  visited: Set<ID>,
): ID | undefined => {
  const present = parentIds.find(isPresent);
  if (present) {
    return present;
  }

  const next = parentIds.flatMap((parentId) => {
    if (visited.has(parentId)) {
      return [];
    }
    visited.add(parentId);

    return resolveParents(parentId);
  });

  return next.length
    ? findPresentAncestorId(next, isPresent, resolveParents, visited)
    : undefined;
};

// Empty = renders as a root. The fallback keeps a goal nested when a filter dropped every parent.
export const getPresentParentIds = (
  goal: Goal,
  isPresent: (id: ID) => boolean,
  { links, goalsById }: GetPresentParentIdsOptions,
): ID[] => {
  const parentIds = getGoalParentIds(goal, { links });
  const presentParentIds = parentIds.filter(isPresent);
  if (presentParentIds.length) {
    return presentParentIds;
  }

  if (goalsById) {
    const resolveParents = (id: ID): ID[] => {
      const parent = goalsById.get(id);

      return parent ? getGoalParentIds(parent, { links }) : [];
    };
    const ancestorId = findPresentAncestorId(
      parentIds,
      isPresent,
      resolveParents,
      new Set<ID>([goal.id]),
    );

    if (ancestorId) {
      return [ancestorId];
    }
  }

  // The chain is the only record an unmigrated goal has, and it holds ancestors the walk above
  // cannot reach when they are outside the rendered set. A migrated goal must never read it: the
  // gate ignores it, so a goal moved to top level would snap back under its former parent.
  if (goal.parentLinksMigrated) {
    return [];
  }

  const nearestAncestorId = [...(goal.ancestorIds ?? [])]
    .reverse()
    .find(isPresent);

  return nearestAncestorId ? [nearestAncestorId] : [];
};

// True when a parent has no present representative anywhere — a goal is then ALSO a root, next to nesting under its other present parents.
export const hasUnrepresentedParent = (
  goal: Goal,
  isPresent: (id: ID) => boolean,
  { links, goalsById }: GetPresentParentIdsOptions,
): boolean => {
  const resolveParents = (id: ID): ID[] => {
    const parent = goalsById?.get(id);
    return parent ? getGoalParentIds(parent, { links }) : [];
  };

  // Mirrors getPresentParentIds's own legacy-chain fallback, so it agrees on what counts as resolved.
  const isRepresented = (parentId: ID): boolean => {
    if (isPresent(parentId)) {
      return true;
    }
    if (
      goalsById &&
      findPresentAncestorId(
        [parentId],
        isPresent,
        resolveParents,
        new Set<ID>([goal.id]),
      )
    ) {
      return true;
    }
    if (goal.parentLinksMigrated) {
      return false;
    }
    return [...(goal.ancestorIds ?? [])].reverse().some(isPresent);
  };

  return getGoalParentIds(goal, { links }).some(
    (parentId) => !isRepresented(parentId),
  );
};
